import {
  Activity,
  Building2,
  Cable,
  ChevronRight,
  ContactRound,
  LogOut,
  Menu,
  Monitor,
  ServerCog,
  ShieldCheck,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { configureApiToken } from './api';
import { Empty } from './components';
import { ActivityPage } from './pages/ActivityPage';
import { CompanyDetailPage } from './pages/CompanyDetailPage';
import { CompaniesPage } from './pages/CompaniesPage';
import { ContactsPage } from './pages/ContactsPage';
import { IntegrationsPage } from './pages/IntegrationsPage';
import { DeviceDetailPage } from './pages/DeviceDetailPage';
import { DevicesPage } from './pages/DevicesPage';
import { usePermissions } from './permissions';
import { SystemPage } from './pages/SystemPage';

const links = [
  {
    to: '/companies',
    label: 'Kunden',
    icon: Building2,
    permission: 'core:companies:read',
    group: 'Stammdaten',
  },
  {
    to: '/contacts',
    label: 'Kontakte',
    icon: ContactRound,
    permission: 'core:contacts:read',
    group: 'Stammdaten',
  },
  {
    to: '/devices',
    label: 'Geräte',
    icon: Monitor,
    permission: 'core:devices:read',
    group: 'Stammdaten',
  },
  {
    to: '/integrations',
    label: 'Integrationen',
    icon: Cable,
    permission: 'core:integrations:read',
    group: 'Verwaltung',
  },
  {
    to: '/activity',
    label: 'Änderungsverlauf',
    icon: Activity,
    permission: 'core:audit:read',
    group: 'Verwaltung',
  },
  { to: '/system', label: 'System', icon: ServerCog, permission: '', group: 'Verwaltung' },
];

export function App() {
  const auth = useAuth();
  const can = usePermissions();
  const location = useLocation();
  const [mobile, setMobile] = useState(false);
  if (auth.isLoading)
    return (
      <div className="grid min-h-screen place-items-center text-muted">Anmeldung wird geladen…</div>
    );
  if (auth.error)
    return (
      <div className="grid min-h-screen place-items-center p-4">
        <div className="panel max-w-md p-8">
          <h1 className="font-bold">Anmeldung fehlgeschlagen</h1>
          <p className="mt-2 text-sm text-red-700">{auth.error.message}</p>
          <button className="button mt-4" onClick={() => void auth.signinRedirect()}>
            Erneut anmelden
          </button>
        </div>
      </div>
    );
  if (!auth.isAuthenticated)
    return (
      <div className="grid min-h-screen place-items-center p-4">
        <div className="panel w-full max-w-sm p-8 text-center">
          <div className="mx-auto mb-5 grid h-12 w-12 place-items-center rounded-2xl bg-accent font-black text-white">
            OC
          </div>
          <h1 className="text-2xl font-semibold">ONIG Control</h1>
          <p className="mt-3 text-sm text-muted">
            Kunden, Kontakte und Geräte.
            <br />
            An einem Ort, klar zugeordnet.
          </p>
          <button className="button mt-6 w-full" onClick={() => void auth.signinRedirect()}>
            Mit Authentik anmelden
          </button>
          <div className="mt-5 flex items-center justify-center gap-2 text-xs text-muted">
            <ShieldCheck size={14} />
            Sicher mit Ihrem Unternehmenskonto
          </div>
        </div>
      </div>
    );
  configureApiToken(() => auth.user?.access_token);
  const visible = links.filter((link) => !link.permission || can(link.permission));
  const section = links.find((link) => location.pathname.startsWith(link.to));
  const identity = auth.user?.profile.name ?? auth.user?.profile.email ?? 'Angemeldet';
  const initials = String(identity)
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[248px_1fr]">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:bg-panel focus:p-3"
      >
        Zum Inhalt
      </a>
      {mobile && (
        <button
          className="fixed inset-0 z-30 bg-slate-900/30 lg:hidden"
          aria-label="Navigation schließen"
          onClick={() => setMobile(false)}
        />
      )}
      <aside
        id="app-navigation"
        className={`${mobile ? 'flex' : 'hidden'} fixed inset-y-0 left-0 z-40 w-[248px] flex-col border-r border-line bg-panel lg:sticky lg:top-0 lg:flex lg:h-screen`}
      >
        <div className="flex h-20 items-center gap-3 px-6">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-accent text-sm font-black text-white">
            OC
          </div>
          <div>
            <div className="font-semibold leading-none">ONIG Control</div>
            <div className="mt-1.5 text-xs text-muted">Zentrale Verwaltung</div>
          </div>
        </div>
        <nav aria-label="Hauptnavigation" className="flex-1 px-4">
          {['Stammdaten', 'Verwaltung'].map((group) => (
            <div key={group}>
              <div className="nav-section">{group}</div>
              <div className="space-y-1">
                {visible
                  .filter((link) => link.group === group)
                  .map(({ to, label, icon: Icon }) => (
                    <NavLink
                      key={to}
                      to={to}
                      onClick={() => setMobile(false)}
                      className={({ isActive }) => `app-nav-link ${isActive ? 'active' : ''}`}
                    >
                      <Icon size={18} strokeWidth={1.7} />
                      {label}
                    </NavLink>
                  ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="m-4 rounded-xl border border-line p-3">
          <div className="flex items-center gap-3">
            <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-accent-dark text-xs font-semibold text-accent">
              {initials}
            </div>
            <div className="min-w-0">
              <div className="truncate text-sm font-medium">{identity}</div>
              <div className="truncate text-xs text-muted">{auth.user?.profile.email}</div>
            </div>
          </div>
          <button
            className="mt-3 flex w-full items-center gap-2 rounded-md px-2 py-2 text-xs text-muted hover:bg-slate-100"
            onClick={() => void auth.signoutRedirect()}
          >
            <LogOut size={14} />
            Abmelden
          </button>
        </div>
      </aside>
      <div className="min-w-0">
        <header className="flex h-16 items-center justify-between border-b border-line bg-panel px-5 md:px-8">
          <div className="flex items-center gap-2 text-xs text-muted">
            <span>Control</span>
            <ChevronRight size={13} />
            <span className="font-medium text-slate-700">{section?.label ?? 'Stammdaten'}</span>
          </div>
          <button
            className="button-secondary lg:hidden"
            onClick={() => setMobile(!mobile)}
            aria-label="Navigation"
            aria-controls="app-navigation"
            aria-expanded={mobile}
          >
            {mobile ? <X size={18} /> : <Menu size={18} />}
          </button>
          <span className="hidden text-xs text-muted lg:block">
            Gemeinsame Daten. Klare Zuordnung.
          </span>
        </header>
        <main
          id="main-content"
          tabIndex={-1}
          className="mx-auto min-w-0 max-w-[1600px] px-4 py-7 md:px-8 lg:p-10"
        >
          <Routes>
            <Route path="/devices" element={<DevicesPage />} />
            <Route path="/devices/:id" element={<DeviceDetailPage />} />
            <Route
              path="/companies"
              element={
                can('core:companies:read') ? (
                  <CompaniesPage />
                ) : (
                  <Empty>Keine Berechtigung für Kunden.</Empty>
                )
              }
            />
            <Route
              path="/companies/:id"
              element={
                can('core:companies:read') ? (
                  <CompanyDetailPage />
                ) : (
                  <Empty>Keine Berechtigung für Kunden.</Empty>
                )
              }
            />
            <Route
              path="/contacts"
              element={
                can('core:contacts:read') ? (
                  <ContactsPage />
                ) : (
                  <Empty>Keine Berechtigung für Kontakte.</Empty>
                )
              }
            />
            <Route
              path="/integrations"
              element={
                can('core:integrations:read') ? (
                  <IntegrationsPage />
                ) : (
                  <Empty>Keine Berechtigung für Integrationen.</Empty>
                )
              }
            />
            <Route
              path="/activity"
              element={
                can('core:audit:read') ? (
                  <ActivityPage />
                ) : (
                  <Empty>Keine Berechtigung für den Änderungsverlauf.</Empty>
                )
              }
            />
            <Route path="/system" element={<SystemPage />} />
            <Route path="*" element={<Navigate to={visible[0]?.to ?? '/system'} replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
